// ©2024 Austin App House. All rights reserved.
//
// Converge Checkout.js embedded card payment for "Add Funds".
//
// Checkout.js expects card data in Converge field names (ssl_card_number, ssl_exp_date
// as MMYY, ssl_cvv2cvc2) and sends it browser → Converge without touching our server.
//
import React, { useState } from 'react';
import Notification from '../../notification';
import {
  buildCheckoutPaymentFields,
  confirmConvergeCheckout,
  createConvergeCheckoutToken,
  waitForConvergeCheckout,
} from '../../../utils/convergePayments';
import { isValidFundsAmount } from '../../../utils/validators';

const onlyDigits = (value) => value.replace(/\D/g, '');

const formatCardNumber = (value) => onlyDigits(value)
  .slice(0, 19)
  .replace(/(.{4})/g, '$1 ')
  .trim();

const formatExpiry = (value) => {
  const digits = onlyDigits(value).slice(0, 4);
  if (digits.length < 3) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

const initialCard = {
  cardholderName: '',
  cardNumber: '',
  expiry: '',
  cvv: '',
  zip: '',
};

function splitCardholderName(name) {
  const trimmed = String(name || '').trim();
  if (!trimmed) return { firstName: '', lastName: '' };
  const parts = trimmed.split(/\s+/);
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' ') || parts[0],
  };
}

function splitUserName(user) {
  const full = (user?.firstName || user?.lastName)
    ? `${user?.firstName || ''} ${user?.lastName || ''}`.trim()
    : (user?.name || user?.email || '').trim();
  if (!full) return { firstName: 'Customer', lastName: '' };
  const parts = full.split(/\s+/);
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' ') || parts[0],
  };
}

export default function ConvergeCheckoutPayment({
  amount,
  agreeToFee,
  feeReady,
  disabled,
  onSuccess,
  user,
}) {
  const [card, setCard] = useState(initialCard);
  const [loading, setLoading] = useState(false);
  const [saveCardForLater, setSaveCardForLater] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (field) => (e) => {
    const { value } = e.target;
    setCard((prev) => {
      if (field === 'cardNumber') return { ...prev, cardNumber: formatCardNumber(value) };
      if (field === 'expiry') return { ...prev, expiry: formatExpiry(value) };
      if (field === 'cvv') return { ...prev, cvv: onlyDigits(value).slice(0, 4) };
      if (field === 'zip') return { ...prev, zip: value.slice(0, 10) };
      return { ...prev, [field]: value };
    });
  };

  const validate = () => {
    if (!isValidFundsAmount(amount)) return 'Please enter an amount of at least $0.01';
    if (!feeReady) return 'The platform fee is still loading. Please try again in a moment.';
    if (!agreeToFee) return 'Please agree to the processing fees';
    if (!card.cardholderName.trim()) return 'Please enter the cardholder name';

    const digits = onlyDigits(card.cardNumber);
    if (digits.length < 13 || digits.length > 19) return 'Please enter a valid card number';
    if (!/^\d{2}\/\d{2}$/.test(card.expiry)) return 'Please enter a valid expiration date (MM/YY)';
    if (card.cvv.length < 3) return 'Please enter a valid CVV';
    return null;
  };

  const startCheckout = async (e) => {
    e.preventDefault();

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      Notification('error', { message: validationError });
      return;
    }

    setError(null);
    setLoading(true);

    const [expMonth, expYear] = card.expiry.split('/');
    const cardholder = splitCardholderName(card.cardholderName);
    const fallbackUser = splitUserName(user);

    try {
      const checkout = await waitForConvergeCheckout();
      const token = await createConvergeCheckoutToken({
        amount,
        saveCard: saveCardForLater,
        firstName: cardholder.firstName || fallbackUser.firstName,
        lastName: cardholder.lastName || fallbackUser.lastName,
      });

      const paymentFields = buildCheckoutPaymentFields({
        token,
        cardNumber: onlyDigits(card.cardNumber),
        expMonth,
        expYear,
        cvv: card.cvv,
        firstName: cardholder.firstName || fallbackUser.firstName,
        lastName: cardholder.lastName || fallbackUser.lastName,
        zip: card.zip,
      });

      if (!paymentFields.ssl_exp_date) {
        throw new Error('Please enter a valid expiration date (MM/YY)');
      }

      const callback = {
        onError: (err) => {
          const msg = typeof err === 'string' ? err : (err?.message || 'Checkout.js payment error');
          setError(msg);
          setLoading(false);
          Notification('error', { message: msg });
        },
        onCancelled: () => {
          setLoading(false);
          Notification('error', { message: 'Payment cancelled' });
        },
        onDeclined: (response) => {
          const msg = response?.ssl_result_message
            || response?.errorMessage
            || 'Payment declined';
          setError(msg);
          setLoading(false);
          Notification('error', { message: msg });
        },
        onApproval: async (response) => {
          try {
            await confirmConvergeCheckout({
              amount,
              saveCard: saveCardForLater,
              transaction: response,
            });
            Notification('success', {
              message: saveCardForLater
                ? 'Funds added and card saved for future use!'
                : 'Funds added successfully!',
            });
            setCard(initialCard);
            setError(null);
            if (onSuccess) onSuccess();
          } catch (err) {
            const msg = err.response?.data?.error
              || err.response?.data?.message
              || err.message
              || 'Payment approved but failed to record funds';
            setError(msg);
            Notification('error', { message: msg });
          } finally {
            setLoading(false);
          }
        },
      };

      checkout.pay(paymentFields, callback);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to start Checkout.js payment';
      setError(msg);
      setLoading(false);
      Notification('error', { message: msg });
    }
  };

  if (disabled) return null;

  return (
    <form id="convergeCheckoutForm" onSubmit={startCheckout}>
      <div className="field_row">
        <div className="field_name">
          <label htmlFor="checkout-cardholder">Cardholder Name</label>
        </div>
        <div className="field_block">
          <input
            id="checkout-cardholder"
            type="text"
            placeholder="Name as it appears on card"
            value={card.cardholderName}
            onChange={handleChange('cardholderName')}
            maxLength={80}
            autoComplete="cc-name"
          />
        </div>
      </div>

      <div className="field_row">
        <div className="field_name">
          <label htmlFor="checkout-number">Card Number</label>
        </div>
        <div className="field_block">
          <input
            id="checkout-number"
            type="text"
            inputMode="numeric"
            placeholder="1234 5678 9012 3456"
            value={card.cardNumber}
            onChange={handleChange('cardNumber')}
            autoComplete="cc-number"
          />
        </div>
      </div>

      <div className="field_row">
        <div className="field_name">
          <label htmlFor="checkout-expiry">Expiration Date</label>
        </div>
        <div className="field_block">
          <input
            id="checkout-expiry"
            type="text"
            inputMode="numeric"
            placeholder="MM/YY"
            value={card.expiry}
            onChange={handleChange('expiry')}
            maxLength={5}
            autoComplete="cc-exp"
          />
        </div>
      </div>

      <div className="field_row">
        <div className="field_name">
          <label htmlFor="checkout-cvv">CVV</label>
        </div>
        <div className="field_block">
          <input
            id="checkout-cvv"
            type="text"
            inputMode="numeric"
            placeholder="e.g. 123"
            value={card.cvv}
            onChange={handleChange('cvv')}
            maxLength={4}
            autoComplete="cc-csc"
          />
        </div>
      </div>

      <div className="field_row">
        <div className="field_name">
          <label htmlFor="checkout-zip">Billing Zip</label>
        </div>
        <div className="field_block">
          <input
            id="checkout-zip"
            type="text"
            inputMode="numeric"
            placeholder="Billing zip code"
            value={card.zip}
            onChange={handleChange('zip')}
            autoComplete="cc-postal-code"
          />
        </div>
      </div>

      <div className="field_row">
        <label className="check_btn">
          Save this card for future payments
          <input
            type="checkbox"
            checked={saveCardForLater}
            onChange={() => setSaveCardForLater((prev) => !prev)}
            disabled={loading}
          />
        </label>
      </div>

      {error && (
        <p style={{ color: '#d32f2f', margin: '4px 0', fontSize: '13px' }}>{error}</p>
      )}

      <div className="field_row" style={{ marginTop: '12px' }}>
        <button
          type="submit"
          className="standard_btn light_btn"
          disabled={loading || !isValidFundsAmount(amount) || !feeReady || !agreeToFee}
        >
          {loading ? 'Processing...' : 'Pay with Credit Card'}
        </button>
      </div>
    </form>
  );
}
